<script lang="ts">
  import moment from "moment";
  import Litepicker from "litepicker";
  import PopoverWithButton from "./Popover/PopoverWithButton.svelte";
  import type { PopoverAlignmentOptions } from "@shared/types/PopoverWithButton";
  import type { Writable } from "svelte/store";
  import type { Field } from "svelte-forms2/types";
  import { createEventDispatcher } from "svelte";
  import { onMount } from "svelte";

  type FormField = Writable<Field<string>>;

  export let startDate: string = null;
  export let endDate: string = null;
  export let processDates = true;
  export let runFilters: () => void | undefined = undefined;
  export let defaultButtonLabel: string;
  export let minWidth: string = null;
  export let position: string = "";
  export let maxDate: Date | number | string = "";
  export let minDate: Date | number | string = "";
  export let selectButtonStyle: boolean = false;
  export let extraLabelClasses: string = "";
  export let predefinedRanges: number[] = [];
  export let singleMode: boolean = false;
  export let dateFormat: string = "MM/DD/YYYY";
  export let alignmentOptions: PopoverAlignmentOptions = {
    desktop: { xPosition: "right" },
    mobile: { xPosition: "left" },
  };
  export let currentFilterDate = null;
  export let numberOfMonths: number = 2;
  export let inputId: string = null;
  export let buttonDisabled: boolean = false;
  export let field: FormField = undefined;
  export let onFocus: () => void = null;
  export let testId: string = null;

  const dispatch = createEventDispatcher();

  let chosenStartDate: string = startDate
    ? moment(new Date(startDate).toISOString()).format(dateFormat)
    : null;
  let chosenEndDate: string = endDate
    ? moment(new Date(endDate).toISOString()).format(dateFormat)
    : null;
  let picker: Litepicker = null;
  let datePickerRef: HTMLElement = null;

  let showPopover: boolean = false;
  function openPopover(e: PointerEvent) {
    e.preventDefault();
    showPopover = !showPopover;
    showDatepicker();
  }

  function formatDate(d: string | Date) {
    return moment(d, dateFormat).format(dateFormat).toString();
  }

  function select(d1: string, d2: string) {
    chosenStartDate = formatDate(d1);
    startDate = chosenStartDate;

    if (singleMode) {
      showPopover = false;
      dispatch("forwardEvent");
    } else {
      chosenEndDate = formatDate(d2);
    }
  }

  $: showDatepicker = function () {
    if (!showPopover) return;

    setTimeout(() => {
      picker = new Litepicker({
        element: datePickerRef,
        startDate: chosenStartDate,
        endDate: chosenEndDate,
        singleMode: singleMode,
        inlineMode: true,
        numberOfMonths: numberOfMonths,
        autoApply: true,
        format: dateFormat,
        numberOfColumns: numberOfMonths,
        showTooltip: true,
        onSelect: select,
        minDate: minDate,
        maxDate: maxDate,
      });
    });
  };

  function clear() {
    startDate = chosenStartDate = null;
    endDate = chosenEndDate = null;
    if (runFilters) runFilters();
    showPopover = false;
  }

  function apply(clearCurrentFilterDate = true) {
    if (chosenStartDate) {
      chosenStartDate = formatDate(chosenStartDate);
      if (chosenStartDate === "Invalid date") {
        chosenStartDate = null;
      }
    }

    if (chosenEndDate) {
      chosenEndDate = formatDate(chosenEndDate);
      if (chosenEndDate === "Invalid date") {
        chosenEndDate = null;
      }
    }
    if (clearCurrentFilterDate) currentFilterDate = null;
    startDate = chosenStartDate;
    endDate = chosenEndDate;
    if (runFilters) runFilters();
    showPopover = false;
  }

  $: labelForButton = function () {
    if (!startDate && !endDate) {
      return defaultButtonLabel;
    }

    if (!startDate) startDate = "";
    if (!endDate) endDate = "";

    if (singleMode) {
      return startDate;
    }

    return `${startDate} - ${endDate}`;
  };

  const setFilterDates = (daysAgo: number) => {
    currentFilterDate = daysAgo;
    chosenStartDate = moment().subtract(daysAgo, "days").format("MM/DD/YYYY");
    chosenEndDate = moment().format("MM/DD/YYYY");
    apply(false);
  };

  const onPredefinedRangeClick = (e, predefinedRange) => {
    setFilterDates(predefinedRange);
  };

  onMount(() => {
    if (dateFormat === "MM/DD/YYYY" && processDates) {
      if (!!startDate) startDate = moment(startDate).format(dateFormat);
      if (!!endDate) endDate = moment(endDate).format(dateFormat);
    }
  });

</script>

<style>
  hr {
    color: #eaeaea;
    border: 0.5px solid #eaeaea;
    background-color: #eaeaea;
  }

  .predefined-range {
    margin-right: 0.5rem;
  }

  .date-helper-link {
    text-decoration: none;
    color: inherit;
  }

  .active-date-helper-link {
    text-decoration: underline;
    font-weight: bold;
  }

  .predefined-ranges span:last-child {
    margin: 0;
  }

</style>

<PopoverWithButton
  bind:show={showPopover}
  {inputId}
  arrow={false}
  buttonLabel={labelForButton()}
  {openPopover}
  {minWidth}
  {position}
  icon={{ type: "ArrowDown", width: "12", height: "12" }}
  {selectButtonStyle}
  {extraLabelClasses}
  {alignmentOptions}
  {buttonDisabled}
  {field}
  {onFocus}
  objectName={testId}
>
  <div style="padding: 16px;">
    <div bind:this={datePickerRef} class="fizz-stack-16" />
    {#if !singleMode}
      <hr class="fizz-stack-16" style="margin-left:-16px;margin-right:-16px;" />
      <div class="fizz-flex-row-responsive fizz-space-between fizz-stack-16">
        <div class="fizz-space-between">
          <span>
            <label class="fizz-label fizz-stack-4" for="input-text">From</label>
            <input
              type="text"
              id="start-date"
              data-testid="start-date"
              bind:value={chosenStartDate}
              class="fizz-input-text"
              style="width: 125px;"
              placeholder="MM/DD/YYYY"
            />
          </span>
          <span>
            <label class="fizz-label fizz-stack-4" for="input-text">To</label>
            <input
              type="text"
              id="end-date"
              data-testid="end-date"
              bind:value={chosenEndDate}
              class="fizz-input-text"
              style="width: 125px;"
              placeholder="MM/DD/YYYY"
            />
          </span>
        </div>

        <div style="padding-top: 20px;">
          <button
            class="fizz-button-secondary float-right"
            style="margin-left: 20px;"
            on:click={() => apply()}
            disabled={!chosenStartDate && !chosenEndDate}
          >
            Apply
          </button>
          <button class="fizz-button float-right" on:click={clear}> Clear </button>
        </div>
      </div>
    {/if}
  </div>
</PopoverWithButton>

{#if predefinedRanges.length}
  <div class="fizz-flex-item">
    <p class="predefined-ranges">
      Last
      {#each predefinedRanges as predefinedRange}
        <span class="predefined-range"
          >`
          <a
            class="date-helper-link"
            class:active-date-helper-link={currentFilterDate === predefinedRange}
            href={"#"}
            on:click|preventDefault={e => onPredefinedRangeClick(e, predefinedRange)}
            >{predefinedRange}</a
          >
        </span>
      {/each}
      days
    </p>
  </div>
{/if}
